import { useEffect, useRef, useState } from 'react'
import type { Locale } from '../i18n/locale'

// Interactive 3D gable-roof viewport — ported from HandyConvert's roof-pitch
// page. A hand-rolled orthographic projection (yaw → tilt → roll) of a 10-vertex
// gable house, with drag-to-rotate, shift-drag pan, wheel zoom, an XYZ gizmo
// (click = 45° snap, hold = spin), double-click / button reset, and undo. The
// house's half-run scales with the building run so the pitch angle reads true.

interface View { yaw: number; tilt: number; roll: number; zoom: number; panX: number; panY: number }
const DEFAULT_VIEW: View = { yaw: 0.42, tilt: 0.2, roll: 0, zoom: 1, panX: 0, panY: 0 }

// world [x,y,z] (y up, z forward) → SVG point, origin (160,100) of a 320×200 box
function project(vx: number, vy: number, vz: number, view: View) {
  const cy = Math.cos(view.yaw), sy = Math.sin(view.yaw)
  const x1 = vx * cy - vz * sy
  const z1 = vx * sy + vz * cy
  const ct = Math.cos(view.tilt), st = Math.sin(view.tilt)
  const y2 = vy * ct - z1 * st
  const z2 = vy * st + z1 * ct
  const cr = Math.cos(view.roll), sr = Math.sin(view.roll)
  const x3 = x1 * cr - y2 * sr
  const y3 = x1 * sr + y2 * cr
  return { x: 160 + x3 * view.zoom + view.panX, y: 100 - y3 * view.zoom + view.panY, z: z2 }
}

type Face = { id: string; points: string; avgZ: number }

function buildScene(pitch: number, run: number, view: View) {
  const W = run > 0 ? run * 5.6 : 90 // half-run in SVG units, scales with the building run
  const H = 18 // wall height (fixed — not a user input)
  const D = 46 // half-depth (fixed)
  const R = Math.max(0, pitch) / 12 * W // ridge rise above the eave
  const p = (x: number, y: number, z: number) => project(x, y, z, view)
  const v = {
    FEL: p(-W, 0, -D), FER: p(W, 0, -D), FBL: p(-W, -H, -D), FBR: p(W, -H, -D), FR: p(0, R, -D),
    BEL: p(-W, 0, D), BER: p(W, 0, D), BBL: p(-W, -H, D), BBR: p(W, -H, D), BR: p(0, R, D),
  }
  const raw: { id: string; verts: { x: number; y: number; z: number }[] }[] = [
    { id: 'wall-front', verts: [v.FR, v.FEL, v.FBL, v.FBR, v.FER] },
    { id: 'wall-back', verts: [v.BR, v.BER, v.BBR, v.BBL, v.BEL] },
    { id: 'wall-right', verts: [v.FER, v.FBR, v.BBR, v.BER] },
    { id: 'wall-left', verts: [v.FEL, v.FBL, v.BBL, v.BEL] },
    { id: 'roof-right', verts: [v.FR, v.FER, v.BER, v.BR] },
    { id: 'roof-left', verts: [v.FR, v.FEL, v.BEL, v.BR] },
  ]
  const faces: Face[] = raw.map((f) => ({
    id: f.id,
    avgZ: f.verts.reduce((s, pt) => s + pt.z, 0) / f.verts.length,
    points: f.verts.map((pt) => `${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' '),
  }))
  // Painter's algorithm: farther faces (larger z) drawn first, closer overlay.
  faces.sort((a, b) => b.avgZ - a.avgZ)
  return { faces, v }
}

// RenoSheets green palette (HandyConvert used sky-blue). Walls neutral, roof green.
const WALL_FILL: Record<string, string> = {
  'wall-front': '#ffffff', 'wall-back': '#ffffff', 'wall-left': '#f1f5f9', 'wall-right': '#f1f5f9',
}
const GIZMO = { x: { x: 22, y: 0 }, y: { x: 0, y: -22 }, z: { x: -15.5, y: 15.5 } }

interface Props { pitch: number; run: number; locale: Locale }

export default function RoofPitch3D({ pitch, run, locale }: Props) {
  const [view, setView] = useState<View>(DEFAULT_VIEW)
  const viewRef = useRef(view)
  const setV = (updater: (v: View) => View) =>
    setView((prev) => { const next = updater(prev); viewRef.current = next; return next })

  // Undo history (snapshot before each meaningful change). Kept in a ref; a
  // length mirror in state drives the Undo button's disabled state.
  const historyRef = useRef<View[]>([])
  const [historyLen, setHistoryLen] = useState(0)
  const pushHistory = () => {
    historyRef.current.push({ ...viewRef.current })
    if (historyRef.current.length > 30) historyRef.current.shift()
    setHistoryLen(historyRef.current.length)
  }
  const undo = () => {
    const prev = historyRef.current.pop()
    if (prev) { viewRef.current = prev; setView(prev) }
    setHistoryLen(historyRef.current.length)
  }
  const resetView = () => { pushHistory(); viewRef.current = DEFAULT_VIEW; setView(DEFAULT_VIEW) }

  // --- drag: rotate (or pan with shift) ---
  const drag = useRef({ active: false, mode: 'rotate', x0: 0, y0: 0, yaw0: 0, tilt0: 0, panX0: 0, panY0: 0 })
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    pushHistory()
    const v = viewRef.current
    drag.current = { active: true, mode: e.shiftKey ? 'pan' : 'rotate', x0: e.clientX, y0: e.clientY, yaw0: v.yaw, tilt0: v.tilt, panX0: v.panX, panY0: v.panY }
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* noop */ }
  }
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current
    if (!d.active) return
    const dx = e.clientX - d.x0, dy = e.clientY - d.y0
    if (d.mode === 'pan') setV((v) => ({ ...v, panX: d.panX0 + dx, panY: d.panY0 + dy }))
    else setV((v) => ({ ...v, yaw: d.yaw0 + dx * 0.012, tilt: Math.max(-1.4, Math.min(1.4, d.tilt0 - dy * 0.012)) }))
  }
  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    drag.current.active = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
  }

  // --- wheel zoom (native non-passive listener so preventDefault works) ---
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wheelRef = useRef<(e: WheelEvent) => void>(() => {})
  wheelRef.current = (e: WheelEvent) => {
    e.preventDefault()
    pushHistory()
    const factor = e.deltaY < 0 ? 1.1 : 0.9
    setV((v) => ({ ...v, zoom: Math.max(0.4, Math.min(3, v.zoom * factor)) }))
  }
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const h = (e: WheelEvent) => wheelRef.current(e)
    svg.addEventListener('wheel', h, { passive: false })
    return () => svg.removeEventListener('wheel', h)
  }, [])

  // --- gizmo: quick click = 45° step, press-and-hold = continuous spin ---
  const holdTimer = useRef<number | null>(null)
  const spinTimer = useRef<number | null>(null)
  const holdAxis = useRef<string | null>(null)
  const holdStart = useRef(0)
  const rotStep = (axis: string, amt: number) =>
    setV((v) => axis === 'x' ? { ...v, tilt: v.tilt + amt } : axis === 'y' ? { ...v, yaw: v.yaw + amt } : { ...v, roll: v.roll + amt })
  const startHold = (axis: string, e: React.PointerEvent) => {
    e.stopPropagation()
    pushHistory()
    holdAxis.current = axis
    holdStart.current = Date.now()
    try { (e.target as Element).setPointerCapture(e.pointerId) } catch { /* noop */ }
    if (holdTimer.current) window.clearTimeout(holdTimer.current)
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null
      if (spinTimer.current) window.clearInterval(spinTimer.current)
      spinTimer.current = window.setInterval(() => rotStep(axis, 0.05), 16)
    }, 200)
  }
  const endHold = (e?: React.PointerEvent) => {
    if (e) e.stopPropagation()
    if (holdTimer.current) { window.clearTimeout(holdTimer.current); holdTimer.current = null }
    if (spinTimer.current) { window.clearInterval(spinTimer.current); spinTimer.current = null }
    const quick = Date.now() - holdStart.current < 200
    if (quick && holdAxis.current) rotStep(holdAxis.current, Math.PI / 4)
    holdAxis.current = null
  }
  // Clear any running timers if the component unmounts mid-spin.
  useEffect(() => () => {
    if (holdTimer.current) window.clearTimeout(holdTimer.current)
    if (spinTimer.current) window.clearInterval(spinTimer.current)
  }, [])

  const { faces, v } = buildScene(pitch, run, view)
  const angleDeg = (Math.atan(Math.max(0, pitch) / 12) * 180) / Math.PI
  const caption = locale === 'fr'
    ? 'Glissez pour pivoter · molette pour zoomer · double-clic pour réinitialiser'
    : 'Drag to rotate · scroll to zoom · double-click to reset'

  const btn = 'rounded border border-border bg-surface px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand-text shadow-sm transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-35'

  return (
    <figure className="w-full">
      <svg
        ref={svgRef}
        viewBox="0 0 320 200"
        preserveAspectRatio="xMidYMid meet"
        className="block w-full touch-none select-none"
        style={{ cursor: 'grab' }}
        role="img"
        aria-label={`Interactive 3D roof at ${pitch}/12, ${angleDeg.toFixed(1)} degrees`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={resetView}
      >
        {faces.map((f) => {
          const isRoof = f.id.startsWith('roof')
          return (
            <polygon
              key={f.id}
              points={f.points}
              fill={isRoof ? '#86efac' : WALL_FILL[f.id]}
              fillOpacity={isRoof ? 0.85 : 1}
              stroke={isRoof ? '#166534' : '#94a3b8'}
              strokeWidth={isRoof ? 1.5 : 1.25}
              strokeLinejoin="miter"
            />
          )
        })}
        {/* Ridge dot at the front gable peak */}
        <circle cx={v.FR.x} cy={v.FR.y} r={4} fill="#166534" stroke="#fff" strokeWidth={1.5} />
        {/* Pitch label along the front barge rafter */}
        <text
          x={(v.FR.x + v.FER.x) / 2 + 10}
          y={(v.FR.y + v.FER.y) / 2 - 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="#166534"
        >
          {pitch}/12
        </text>
        <text x={v.FER.x + 4} y={v.FER.y + 14} fontSize="9.5" fill="#475569">
          {angleDeg.toFixed(1)}°
        </text>
      </svg>

      {/* Controls sit BELOW the diagram (off the 3D house) — gizmo on the left,
          Undo/Reset on the right. */}
      <div className="no-print mt-3 flex items-center justify-between gap-3">
        {/* XYZ axis gizmo — click an axis to snap 45°, hold to spin */}
        <svg className="h-14 w-14 shrink-0 overflow-visible rounded border border-border bg-surface p-0.5" viewBox="-30 -30 60 60" aria-hidden="true">
          <line x1="0" y1="0" x2={GIZMO.x.x} y2={GIZMO.x.y} stroke="#ef4444" strokeWidth={2} strokeLinecap="round" />
          <line x1="0" y1="0" x2={GIZMO.y.x} y2={GIZMO.y.y} stroke="#22c55e" strokeWidth={2} strokeLinecap="round" />
          <line x1="0" y1="0" x2={GIZMO.z.x} y2={GIZMO.z.y} stroke="#3b82f6" strokeWidth={2} strokeLinecap="round" />
          {(['x', 'y', 'z'] as const).map((axis) => (
            <circle
              key={axis}
              cx={GIZMO[axis].x}
              cy={GIZMO[axis].y}
              r={8}
              fill={axis === 'x' ? '#ef4444' : axis === 'y' ? '#22c55e' : '#3b82f6'}
              stroke="#fff"
              strokeWidth={1.5}
              style={{ cursor: 'pointer' }}
              onPointerDown={(e) => startHold(axis, e)}
              onPointerUp={(e) => endHold(e)}
              onPointerCancel={(e) => endHold(e)}
            >
              <title>{locale === 'fr' ? 'Clic = 45° · maintenir = tourner' : 'Click = 45° · hold = spin'}</title>
            </circle>
          ))}
          <text x={GIZMO.x.x} y={GIZMO.x.y + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none' }}>X</text>
          <text x={GIZMO.y.x} y={GIZMO.y.y + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none' }}>Y</text>
          <text x={GIZMO.z.x} y={GIZMO.z.y + 3} textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" style={{ pointerEvents: 'none' }}>Z</text>
        </svg>
        <div className="flex gap-1.5">
          <button type="button" className={btn} onClick={undo} disabled={historyLen === 0} aria-label={locale === 'fr' ? 'Annuler' : 'Undo'}>
            ↶ {locale === 'fr' ? 'Annuler' : 'Undo'}
          </button>
          <button type="button" className={btn} onClick={resetView} aria-label={locale === 'fr' ? 'Réinitialiser' : 'Reset'}>
            ⤴ {locale === 'fr' ? 'Réinit.' : 'Reset'}
          </button>
        </div>
      </div>

      <figcaption className="mt-2 text-center text-xs text-text-subtle">{caption}</figcaption>
    </figure>
  )
}
